import { T, useTranslation } from "@redotech/redo-i18n";
import { useReturnsSettingsParentHref } from "@redotech/redo-merchant-app-common/settings/returns-settings-parent-path";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import type { CombineLabelState } from "@redotech/redo-return-core-common/return-flow-step-valid";
import { StepTypeDetailsProps } from "@redotech/redo-return-ui/flows/step";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext } from "react";
import { Link } from "react-router-dom";

export const CombineLabelDetails = memo(function CombineLabelDetails(
  _props: StepTypeDetailsProps<CombineLabelState>,
) {
  const { t } = useTranslation();
  const team = useContext(TeamContext);
  const labelCombinationHref = useReturnsSettingsParentHref("shipping");

  const skuGroupingEnabled =
    team?.settings.returns?.labelCombination?.combineLabelsSkuGroups?.enabled ??
    false;
  const onlySameFulfillment =
    team?.settings.returns?.labelCombination
      ?.onlyCombineLabelsFromSameFulfillment ?? false;

  return (
    <Flex dir="column" gap="md">
      {!skuGroupingEnabled && !onlySameFulfillment && (
        <Text fontSize="xs" fontWeight="medium">
          {t(
            "• All items that are being returned to the same location will share a single shipping label.",
          )}
        </Text>
      )}
      {(skuGroupingEnabled || onlySameFulfillment) && (
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {t("Combination rules:")}
          </Text>
          {skuGroupingEnabled && (
            <Text fontSize="xs" fontWeight="medium">
              {t("• Only items from your SKU groups will be combined")}
            </Text>
          )}
          {onlySameFulfillment && (
            <Text fontSize="xs" fontWeight="medium">
              {t("• Only items that share a fulfillment will be combined")}
            </Text>
          )}
        </Flex>
      )}

      <Text fontSize="xs" fontWeight="medium" textColor="secondary">
        <T>
          To update the combining rules, navigate to the{" "}
          <Link
            style={{ textDecoration: "underline" }}
            to={labelCombinationHref}
          >
            Label combination
          </Link>{" "}
          section in settings.
        </T>
      </Text>

      <Text fontSize="xs" fontWeight="regular" mt="lg">
        {t(
          "This step will combine applicable products into one shipping label.",
        )}
      </Text>
    </Flex>
  );
});
